/**
 * Economics Summary Result Component
 *
 * Displays:
 * - Hardware costs
 * - Power consumption and annual costs
 * - Cost per usable TB
 * - 5-year TCO projection
 */

import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Stack } from '@mui/material';
import type { EconomicsResults } from '../../types';
import { TooltipIcon } from '../ui';

interface EconomicsSummaryProps {
  results: EconomicsResults;
}

export function EconomicsSummary({ results }: EconomicsSummaryProps) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Economics Summary
      </Typography>

      <Stack spacing={1} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Hardware Cost: <TooltipIcon tooltipKey="hardwareCost" /></Typography>
          <Typography fontWeight="bold">
            ${results.hardwareCost.toFixed(2)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Cost Per Usable TB: <TooltipIcon tooltipKey="costPerTiB" /></Typography>
          <Typography fontWeight="bold">
            ${results.costPerUsableTB.toFixed(2)}/TB
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Total Drives: <TooltipIcon tooltipKey="totalDrives" /></Typography>
          <Typography fontWeight="bold">
            {results.totalDrives}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Idle Power: <TooltipIcon tooltipKey="idlePower" /></Typography>
          <Typography fontWeight="bold">
            {results.totalWattsIdle.toFixed(1)} W
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Load Power: <TooltipIcon tooltipKey="loadPower" /></Typography>
          <Typography fontWeight="bold">
            {results.totalWattsLoad.toFixed(1)} W
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Annual Power: <TooltipIcon tooltipKey="annualKWh" /></Typography>
          <Typography fontWeight="bold">
            {results.annualKWh.toFixed(0)} kWh (${results.annualPowerCost.toFixed(2)}/year)
          </Typography>
        </Box>
      </Stack>

      <Typography variant="subtitle1" gutterBottom>
        5-Year TCO Projection <TooltipIcon tooltipKey="tco" />
      </Typography>

      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell align="right">Expected Failures <TooltipIcon tooltipKey="expectedFailures" /></TableCell>
              <TableCell align="right">Replacement Cost</TableCell>
              <TableCell align="right">Power Cost</TableCell>
              <TableCell align="right">Cumulative</TableCell>
            </TableRow>
            {results.tcoProjection.map((year) => (
              <TableRow key={year.year}>
                <TableCell>{year.year}</TableCell>
                <TableCell align="right">{year.expectedFailures.toFixed(2)}</TableCell>
                <TableCell align="right">${year.replacementCost.toFixed(2)}</TableCell>
                <TableCell align="right">${year.powerCost.toFixed(2)}</TableCell>
                <TableCell align="right">
                  <strong>${year.cumulativeCost.toFixed(2)}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> TCO includes hardware cost, expected drive replacements based on AFR,
          and 5 years of power consumption. Actual costs may vary.
        </Typography>
      </Box>
    </Paper>
  );
}
